<script  setup>
import {reactive, ref, watch, computed} from "vue";
import {ElButton, ElMessage} from "element-plus";
import { moneyTotal, roundMoney } from '../../../utils/money';
import {
    isBandedTypeAddon,
    resolveBandedAddonCode,
    bandedCapacityForCode,
    scaleTransportationPricing,
    transportUnits,
} from '../../../utils/landPricing';
import moment from 'moment';

const props = defineProps({
    form: Object,
    addonTypes: {
        type: Array,
        default: () => [],
    },
    pricingRates: {
        type: Object,
        default: () => ({}),
    },
});
const emit = defineEmits(['canceli', 'added']);
const ruleFormRef = ref();
const eform = props.form;

const addformmodel = ref(true);

const addform = reactive({
    name: '',
    description: '',
    unit_cost: 0,
    pricing: {
        total: 0,
        adult: 0,
        child: 0,
        infant: 0,
        unit_cost: 0,
        units: 0,
    },
});

const isBanded = computed(() =>
    isBandedTypeAddon({ name: addform.name }, props.addonTypes)
);

const bandedCode = computed(() =>
    resolveBandedAddonCode({ name: addform.name }, props.addonTypes)
);

const capacity = computed(() =>
    bandedCapacityForCode(bandedCode.value, props.pricingRates)
);

const unitLabel = computed(() =>
    bandedCode.value === 'transportation' ? 'vehicle' : 'unit'
);

const previewUnits = computed(() =>
    isBanded.value ? transportUnits(eform.adults, capacity.value) : 0
);

watch(
    () => [addform.pricing, addform.name, addform.unit_cost, eform.adults],
    () => {
        updateupricecount();
    },
    { deep: true }
);

const updateupricecount = () => {
    if (isBanded.value) {
        const scaled = scaleTransportationPricing({
            unitCost: addform.unit_cost,
            adults: eform.adults,
            capacity: capacity.value,
        });
        addform.pricing.unit_cost = scaled.unit_cost;
        addform.pricing.units = scaled.units;
        addform.pricing.total = scaled.total;
        addform.pricing.adult = scaled.adult;
        addform.pricing.child = 0;
        addform.pricing.infant = 0;
        return;
    }
    addform.pricing.total = moneyTotal(
        eform.adults,
        addform.pricing.adult,
        eform.childs,
        addform.pricing.child,
        eform.infants,
        addform.pricing.infant
    );
};

const submitForm = async (formEl) => {
    if (!formEl) return;
    await formEl.validate((valid) => {
        if (!valid) return;

        updateupricecount();

        const pricing = {
            total: roundMoney(addform.pricing.total),
            adult: roundMoney(addform.pricing.adult),
            child: roundMoney(addform.pricing.child),
            infant: roundMoney(addform.pricing.infant),
        };

        if (isBanded.value) {
            pricing.unit_cost = roundMoney(addform.unit_cost);
            pricing.units = addform.pricing.units;
        }

        const description = String(addform.description || '').trim();
        const item = {
            id: 'id' + moment().format('x'),
            name: addform.name,
            description: description === '' ? null : description,
            image: '',
            images: [],
            category: bandedCode.value || '',
            pricing,
        };

        eform.addons.push(item);
        addformmodel.value = false;

        ElMessage({
            message: 'Addon Added Sucessfully.',
            type: 'success',
        });

        emit('added');
    });
};
</script>

<template>
    <el-dialog
        v-model="addformmodel"
        title="Add Addon"
        width="50%"
        @closed="emit('canceli')"
    >
        <el-form
            ref="ruleFormRef"
            :model="addform"
            status-icon
            label-width="auto"
            class="demo-ruleForm"
            label-position="top"
            size="small"
        >
            <el-row :gutter="20">
                <el-col :span="24">
                    <el-form-item
                        label="Name"
                        prop="name"
                        :rules="{ required: true, message: 'Required', trigger: ['blur', 'change'] }"
                    >
                        <el-input v-model="addform.name" placeholder="Name" />
                    </el-form-item>
                </el-col>
            </el-row>
            <el-row :gutter="20">
                <el-col :span="24">
                    <el-form-item label="Description">
                        <el-input
                            v-model="addform.description"
                            type="textarea"
                            :rows="3"
                            placeholder="Optional"
                        />
                    </el-form-item>
                </el-col>
            </el-row>

            <template v-if="isBanded">
                <el-alert
                    class="mb-3"
                    :title="`${previewUnits} ${unitLabel}(s) for ${eform.adults} adult(s) (capacity ${capacity})`"
                    type="info"
                    :closable="false"
                    show-icon
                />
                <el-row :gutter="20">
                    <el-col :span="8">
                        <el-form-item
                            :label="'Unit cost (per ' + unitLabel + ')'"
                            prop="unit_cost"
                            :rules="{ required: true, message: 'Required', trigger: ['blur', 'change'] }"
                        >
                            <el-input v-model="addform.unit_cost" placeholder="Unit cost" type="number" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item :label="unitLabel === 'vehicle' ? 'Vehicles' : 'Units'">
                            <el-input :model-value="previewUnits" readonly />
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="Total Pricing">
                            <el-input v-model="addform.pricing.total" type="number" readonly />
                        </el-form-item>
                    </el-col>
                </el-row>
                <p class="text-muted" style="font-size: 12px;">
                    Per adult: ₹{{ addform.pricing.adult }}
                    (total ÷ {{ eform.adults }} adults)
                </p>
            </template>

            <template v-else>
                <el-row :gutter="20">
                    <el-col :span="4">
                        <el-form-item label="Adults">
                            <el-input v-model="eform.adults" placeholder="Adults" readonly />
                        </el-form-item>
                    </el-col>
                    <el-col :span="4">
                        <el-form-item
                            label="Adult Pricing"
                            prop="pricing.adult"
                            :rules="{ required: true, message: 'Required', trigger: ['blur', 'change'] }"
                        >
                            <el-input v-model="addform.pricing.adult" placeholder="Adult Pricing" type="number" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="20" v-if="eform.childs > 0">
                    <el-col :span="4">
                        <el-form-item label="Child">
                            <el-input v-model="eform.childs" placeholder="Child" readonly />
                        </el-form-item>
                    </el-col>
                    <el-col :span="4">
                        <el-form-item
                            label="Child Pricing"
                            prop="pricing.child"
                            :rules="{ required: true, message: 'Required', trigger: ['blur', 'change'] }"
                        >
                            <el-input v-model="addform.pricing.child" placeholder="Child Pricing" type="number" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="20" v-if="eform.infants > 0">
                    <el-col :span="4">
                        <el-form-item label="Infants">
                            <el-input v-model="eform.infants" placeholder="Infants" readonly />
                        </el-form-item>
                    </el-col>
                    <el-col :span="4">
                        <el-form-item
                            label="Infants Pricing"
                            prop="pricing.infant"
                            :rules="{ required: true, message: 'Required', trigger: ['blur', 'change'] }"
                        >
                            <el-input v-model="addform.pricing.infant" placeholder="Infants Pricing" type="number" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="20">
                    <el-col :span="4" />
                    <el-col :span="4">
                        <el-form-item
                            label="Total Pricing"
                            prop="pricing.total"
                            :rules="{ required: true, message: 'Required', trigger: ['blur', 'change'] }"
                        >
                            <el-input v-model="addform.pricing.total" placeholder="Total Pricing" type="number" readonly />
                        </el-form-item>
                    </el-col>
                </el-row>
            </template>
        </el-form>

        <template #footer>
            <div class="dialog-footer">
                <el-button @click="addformmodel = false">Cancel</el-button>
                <el-button type="primary" @click="submitForm(ruleFormRef)">Save</el-button>
            </div>
        </template>
    </el-dialog>
</template>

<style scoped>
</style>
